import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Plus, Trash2, Pencil, X, Upload, Sparkles, Download, Search,
  Building2, TrendingDown, TrendingUp, Minus, AlertCircle, RefreshCw,
} from "lucide-react";
import ItemIntelligencePanel from "./ItemIntelligencePanel";

const EMPTY_PRICE = {
  vendor_id: "", item_name: "", unit: "kg", price: "",
  sku: "", category: "", brand: "", gst_percent: "0",
  effective_date: new Date().toISOString().slice(0, 10),
  city: "", availability: "in_stock", remarks: "", source: "manual",
};

const SOURCE_STYLES = {
  manual: "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]",
  csv: "bg-[hsl(200_50%_90%)] text-[hsl(200_50%_35%)]",
  ai_estimate: "bg-[hsl(280_45%_92%)] text-[hsl(280_50%_45%)]",
  api: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]",
  scrape: "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]",
};

const AVAIL_STYLES = {
  in_stock: "text-[hsl(var(--forest))]",
  limited: "text-[hsl(35_75%_38%)]",
  out_of_stock: "text-[hsl(6_62%_45%)]",
};

export default function BenchmarksTab() {
  const [vendors, setVendors] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterVendor, setFilterVendor] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // "new" | price object
  const [form, setForm] = useState(EMPTY_PRICE);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showVendorMgr, setShowVendorMgr] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [itemPanel, setItemPanel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [vRes, pRes] = await Promise.all([
        api.get("/staff/procurement/benchmark-vendors"),
        api.get("/staff/procurement/benchmark-prices"),
      ]);
      setVendors(vRes.data.vendors || []);
      setPrices(pRes.data.rows || []);
    } catch {
      toast.error("Could not load benchmark data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = prices;
    if (filterVendor) list = list.filter((r) => r.vendor_id === filterVendor);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) => (r.item_name || "").toLowerCase().includes(q) || (r.category || "").toLowerCase().includes(q));
    }
    return list.sort((a, b) => (a.item_name || "").localeCompare(b.item_name || ""));
  }, [prices, filterVendor, search]);

  const seedVendors = async () => {
    try {
      const { data } = await api.post("/staff/procurement/benchmark-vendors/seed");
      toast.success(`Seeded ${data.created} new vendor${data.created === 1 ? "" : "s"} (of ${data.total})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
  };

  const openNew = () => {
    setForm({ ...EMPTY_PRICE, vendor_id: vendors[0]?.id || "" });
    setEditing("new");
  };
  const openEdit = (row) => {
    setForm({
      vendor_id: row.vendor_id, item_name: row.item_name, unit: row.unit,
      price: String(row.price), sku: row.sku || "", category: row.category || "",
      brand: row.brand || "", gst_percent: String(row.gst_percent || 0),
      effective_date: row.effective_date || new Date().toISOString().slice(0, 10),
      city: row.city || "", availability: row.availability || "in_stock",
      remarks: row.remarks || "", source: row.source || "manual", _id: row.id,
    });
    setEditing(row);
  };

  const save = async () => {
    if (!form.vendor_id) return toast.error("Pick a benchmark vendor");
    if (!form.item_name.trim()) return toast.error("Item name is required");
    const price = parseFloat(form.price);
    if (!price || price <= 0) return toast.error("Enter a valid price > 0");
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/staff/procurement/benchmark-prices", {
          vendor_id: form.vendor_id, item_name: form.item_name.trim(),
          unit: form.unit, price, sku: form.sku, category: form.category,
          brand: form.brand, gst_percent: parseFloat(form.gst_percent) || 0,
          effective_date: form.effective_date, city: form.city,
          availability: form.availability, remarks: form.remarks, source: "manual",
        });
        toast.success("Benchmark price added");
      } else {
        await api.patch(`/staff/procurement/benchmark-prices/${form._id}`, {
          price, unit: form.unit, sku: form.sku, category: form.category,
          brand: form.brand, gst_percent: parseFloat(form.gst_percent) || 0,
          effective_date: form.effective_date, city: form.city,
          availability: form.availability, remarks: form.remarks,
        });
        toast.success("Updated");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete benchmark ${row.item_name} @ ₹${row.price}?`)) return;
    try {
      await api.delete(`/staff/procurement/benchmark-prices/${row.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/staff/procurement/benchmark-prices/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "benchmark-prices-template.csv";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };

  const uploadCSV = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/staff/procurement/benchmark-prices/upload-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const msg = `Imported ${data.inserted} of ${data.total_rows} rows`;
      if (data.errors?.length) {
        toast.warning(`${msg}. ${data.errors.length} row${data.errors.length === 1 ? "" : "s"} skipped — check console.`);
        console.warn("Benchmark CSV errors:", data.errors);
      } else {
        toast.success(msg);
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  if (loading) return <div className="text-sm text-[hsl(var(--ink-soft))]">Loading benchmarks…</div>;

  if (vendors.length === 0) {
    return (
      <div data-testid="benchmarks-empty" className="mt-4 border border-dashed border-[hsl(var(--rule))] p-8 text-center bg-[hsl(var(--bg-2))]">
        <Building2 size={32} className="mx-auto text-[hsl(var(--ink-soft))]" />
        <h3 className="mt-3 display text-xl">No benchmark vendors yet</h3>
        <p className="mt-1 text-sm text-[hsl(var(--ink-soft))] max-w-md mx-auto">
          Seed the default vendors (Hyperpure, Metro, Reliance Market, Udaan, Local Wholesale) to start comparing supplier prices in your POs.
        </p>
        <button data-testid="benchmark-seed-btn" onClick={seedVendors} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-[hsl(var(--forest))] text-[hsl(var(--bg))] text-sm">
          <Plus size={14} /> Seed default vendors
        </button>
      </div>
    );
  }

  return (
    <div data-testid="benchmarks-tab" className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-2.5 text-[hsl(var(--ink-soft))]" />
          <input
            data-testid="benchmark-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item or category…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))]"
          />
        </div>
        <select
          data-testid="benchmark-filter-vendor"
          value={filterVendor}
          onChange={(e) => setFilterVendor(e.target.value)}
          className="text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-3 py-2"
        >
          <option value="">All vendors</option>
          {vendors.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
        </select>
        <button
          data-testid="benchmark-new"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-[hsl(var(--forest))] text-[hsl(var(--bg))]"
        >
          <Plus size={12} /> Add price
        </button>
        <label className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[hsl(var(--rule))] cursor-pointer hover:bg-[hsl(var(--bg-2))]">
          <Upload size={12} /> {uploading ? "Uploading…" : "Upload CSV"}
          <input
            data-testid="benchmark-upload"
            type="file"
            accept=".csv"
            className="hidden"
            disabled={uploading}
            onChange={(e) => { if (e.target.files?.[0]) { uploadCSV(e.target.files[0]); e.target.value = ""; } }}
          />
        </label>
        <button
          data-testid="benchmark-template"
          onClick={downloadTemplate}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg-2))]"
        >
          <Download size={12} /> Template
        </button>
        <button
          data-testid="benchmark-ai"
          onClick={() => setAiOpen(true)}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[hsl(280_50%_45%)] text-[hsl(280_50%_45%)] hover:bg-[hsl(280_45%_97%)]"
        >
          <Sparkles size={12} /> AI estimate
        </button>
        <button
          data-testid="benchmark-vendor-mgr"
          onClick={() => setShowVendorMgr(!showVendorMgr)}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg-2))]"
        >
          <Building2 size={12} /> Vendors ({vendors.length})
        </button>
      </div>

      {showVendorMgr && <VendorManager vendors={vendors} onChanged={load} onClose={() => setShowVendorMgr(false)} />}

      <div className="border border-[hsl(var(--rule))]">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] text-xs uppercase tracking-wider sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
            <tr>
              <th className="text-left p-2">Item</th>
              <th className="text-left p-2">Vendor</th>
              <th className="text-left p-2">Unit</th>
              <th className="text-right p-2">Price</th>
              <th className="text-left p-2">Effective</th>
              <th className="text-left p-2">Source</th>
              <th className="text-left p-2">Avail.</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--rule))]">
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-[hsl(var(--ink-soft))]">No benchmark prices yet. Add manually, upload CSV, or use the AI estimate.</td></tr>
            ) : filtered.map((row) => (
              <tr key={row.id} className="hover:bg-[hsl(var(--bg-2))]" data-testid={`benchmark-row-${row.id}`}>
                <td className="p-2">
                  <button
                    data-testid={`benchmark-item-detail-${row.id}`}
                    onClick={() => setItemPanel({ item_name: row.item_name, unit: row.unit })}
                    className="font-medium hover:underline text-left"
                  >
                    {row.item_name}
                  </button>
                  {row.category && <div className="text-xs text-[hsl(var(--ink-soft))]">{row.category}{row.brand ? ` · ${row.brand}` : ""}</div>}
                </td>
                <td className="p-2 text-xs text-[hsl(var(--ink-soft))]">{row.vendor_name}</td>
                <td className="p-2 text-xs">{row.unit}</td>
                <td className="p-2 text-right font-mono">₹{Number(row.price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                <td className="p-2 text-xs text-[hsl(var(--ink-soft))]">{row.effective_date}</td>
                <td className="p-2">
                  <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded ${SOURCE_STYLES[row.source] || SOURCE_STYLES.manual}`}>
                    {row.source}
                  </span>
                </td>
                <td className={`p-2 text-xs ${AVAIL_STYLES[row.availability] || ""}`}>{row.availability}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(row)} className="p-1.5 hover:bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]" data-testid={`benchmark-edit-${row.id}`}><Pencil size={12} /></button>
                  <button onClick={() => del(row)} className="p-1.5 hover:bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]" data-testid={`benchmark-delete-${row.id}`}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-[hsl(var(--ink-soft))]">
        Showing latest price per (vendor × item × unit). Older rows are kept for history and used by PO snapshots.
      </div>

      {editing && (
        <PriceForm
          form={form} setForm={setForm} vendors={vendors} saving={saving}
          isNew={editing === "new"} onSave={save} onClose={() => setEditing(null)}
        />
      )}
      {aiOpen && (
        <AIEstimateModal vendors={vendors} onClose={() => setAiOpen(false)} onDone={load} />
      )}
      {itemPanel && (
        <ItemIntelligencePanel
          itemName={itemPanel.item_name}
          unit={itemPanel.unit}
          onClose={() => setItemPanel(null)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------
// Manual price add / edit
// -----------------------------------------------------------
function PriceForm({ form, setForm, vendors, saving, isNew, onSave, onClose }) {
  return (
    <div data-testid="benchmark-form-modal" className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[hsl(var(--rule))]">
          <h3 className="display text-lg">{isNew ? "Add benchmark price" : "Edit benchmark price"}</h3>
          <button onClick={onClose} className="p-1.5 text-[hsl(var(--ink-soft))]"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Vendor</label>
              <select
                data-testid="benchmark-form-vendor"
                value={form.vendor_id}
                disabled={!isNew}
                onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5"
              >
                {vendors.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Item name</label>
              <input
                data-testid="benchmark-form-item"
                value={form.item_name}
                disabled={!isNew}
                onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5"
                placeholder="e.g. Tomato"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Unit</label>
              <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5">
                {["kg","g","litre","ml","pcs","bunch","dozen","packet","bottle"].map((u) => (<option key={u}>{u}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Price (₹)</label>
              <input
                data-testid="benchmark-form-price"
                type="number"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">SKU</label>
              <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Category</label>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Brand</label>
              <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">GST %</label>
              <input type="number" step="0.01" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Effective date</label>
              <input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">City</label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Availability</label>
              <select value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5">
                <option value="in_stock">In stock</option>
                <option value="limited">Limited</option>
                <option value="out_of_stock">Out of stock</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Remarks</label>
              <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
          <button onClick={onClose} className="text-xs px-3 py-2 border border-[hsl(var(--rule))]">Cancel</button>
          <button
            data-testid="benchmark-form-save"
            disabled={saving}
            onClick={onSave}
            className="text-xs px-4 py-2 bg-[hsl(var(--forest))] text-[hsl(var(--bg))] disabled:opacity-50"
          >
            {saving ? "Saving…" : (isNew ? "Add" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------
// AI estimate modal — feeds Claude a list of items → gets price grid
// -----------------------------------------------------------
function AIEstimateModal({ vendors, onClose, onDone }) {
  const [itemsText, setItemsText] = useState("Tomato\nOnion\nPotato\nGinger\nGarlic");
  const [city, setCity] = useState("Wellington, Nilgiris, Tamil Nadu");
  const [unitHint, setUnitHint] = useState("kg");
  const [selVendors, setSelVendors] = useState(vendors.map((v) => v.id));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const items = itemsText.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    if (!items.length) return toast.error("Enter at least one item");
    if (items.length > 25) return toast.error("Max 25 items per run — split into batches");
    setBusy(true);
    try {
      const { data } = await api.post("/staff/procurement/benchmark-prices/ai-estimate", {
        item_names: items,
        vendor_ids: selVendors.length ? selVendors : undefined,
        city, unit_hint: unitHint,
      });
      setResult(data);
      toast.success(`Inserted ${data.inserted} AI-estimated row${data.inserted === 1 ? "" : "s"}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI estimate failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="benchmark-ai-modal" className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[hsl(var(--rule))]">
          <h3 className="display text-lg flex items-center gap-2"><Sparkles size={16} className="text-[hsl(280_50%_45%)]" /> AI benchmark estimate</h3>
          <button onClick={onClose} className="p-1.5 text-[hsl(var(--ink-soft))]"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="p-3 bg-[hsl(280_45%_97%)] border border-[hsl(280_45%_88%)] text-xs text-[hsl(280_50%_35%)]">
            <AlertCircle size={12} className="inline mr-1" />
            Claude Sonnet 4.6 will <b>estimate</b> current wholesale prices based on general market knowledge — this is <b>not</b> a live scrape. Verify before making purchase decisions. Rows are tagged <code>source=ai_estimate</code> so you can filter/replace them later.
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Items (one per line, max 25)</label>
            <textarea
              data-testid="benchmark-ai-items"
              value={itemsText}
              onChange={(e) => setItemsText(e.target.value)}
              rows={8}
              className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5 font-mono"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">City / region</label>
              <input value={city} onChange={(e) => setCity(e.target.value)} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Default unit</label>
              <select value={unitHint} onChange={(e) => setUnitHint(e.target.value)} className="w-full text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5">
                {["kg","g","litre","ml","pcs","bunch","dozen"].map((u) => (<option key={u}>{u}</option>))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">Vendors ({selVendors.length}/{vendors.length})</label>
            <div className="flex flex-wrap gap-1.5">
              {vendors.map((v) => {
                const on = selVendors.includes(v.id);
                return (
                  <button
                    key={v.id}
                    onClick={() => setSelVendors(on ? selVendors.filter((x) => x !== v.id) : [...selVendors, v.id])}
                    className={`text-xs px-2 py-1 border ${on ? "border-[hsl(var(--forest))] bg-[hsl(var(--forest))]/10 text-[hsl(var(--forest))]" : "border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]"}`}
                  >
                    {v.name}
                  </button>
                );
              })}
            </div>
          </div>
          {result && (
            <div className="p-3 bg-[hsl(148_40%_95%)] border border-[hsl(148_40%_80%)] text-xs">
              Inserted <b>{result.inserted}</b> row{result.inserted === 1 ? "" : "s"} tagged <code>ai_estimate</code>.
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
          <button onClick={onClose} className="text-xs px-3 py-2 border border-[hsl(var(--rule))]">Close</button>
          <button
            data-testid="benchmark-ai-run"
            disabled={busy}
            onClick={run}
            className="text-xs px-4 py-2 bg-[hsl(280_50%_45%)] text-white disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {busy ? "Estimating…" : "Run AI estimate"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------
// Vendor manager (inline, above the table)
// -----------------------------------------------------------
function VendorManager({ vendors, onChanged, onClose }) {
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/staff/procurement/benchmark-vendors", { name });
      setNewName("");
      onChanged();
      toast.success("Vendor added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (v) => {
    try {
      await api.patch(`/staff/procurement/benchmark-vendors/${v.id}`, { active: !v.active });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const del = async (v) => {
    if (!window.confirm(`Remove ${v.name}?`)) return;
    try {
      await api.delete(`/staff/procurement/benchmark-vendors/${v.id}`);
      onChanged();
      toast.success("Removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="benchmark-vendor-mgr" className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
      <div className="flex items-center justify-between p-3 border-b border-[hsl(var(--rule))]">
        <div className="text-sm font-semibold">Benchmark vendors</div>
        <button onClick={onClose} className="text-xs text-[hsl(var(--ink-soft))]"><X size={14} /></button>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Add vendor (e.g. Amazon Fresh)"
            className="flex-1 text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] px-2 py-1.5"
          />
          <button onClick={add} disabled={busy} className="text-xs px-3 py-1.5 bg-[hsl(var(--forest))] text-[hsl(var(--bg))]"><Plus size={12} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {vendors.map((v) => (
            <div key={v.id} className="flex items-center gap-2 p-2 border border-[hsl(var(--rule))] bg-[hsl(var(--bg))]">
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{v.name}</div>
                {v.notes && <div className="text-[10px] text-[hsl(var(--ink-soft))] truncate">{v.notes}</div>}
              </div>
              <label className="text-[10px] flex items-center gap-1">
                <input type="checkbox" checked={v.active} onChange={() => toggle(v)} /> {v.active ? "on" : "off"}
              </label>
              <button onClick={() => del(v)} className="text-[hsl(6_62%_45%)]"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
